import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {AbstractDataProvider} from '../AbstractDataProvider';
import {NgxUiLoaderService} from 'ngx-ui-loader';
import {forkJoin, Subject, Subscription} from 'rxjs';
import {AuthServiceCustom} from 'src/shared/service/auth.service';
import {GenericFilterModel, QueryStringParam} from '../security/QueryStringParam';
import {SortDescriptor} from '@progress/kendo-data-query';
import {EnUserTypes} from '../security/page-name.enum';
import {ColumnSettings} from 'src/shared/model/column-settings';
import {getErrorMessageText, toUpperCase} from '../helper';
import {debounceTime, finalize, map} from 'rxjs/operators';
import {ClientSearchItemResponseModel, ResponseModel} from '../../shared/model/interfaces';
import {DictionaryPipe} from '../../shared/pipes/dictionary.pipe';

enum Field {
  TypeOfClient = 'TypeOfClient',
  Fund = 'Fund',
  ClientNumber = 'ClientNumber', // Investor or client
  ClientName = 'ClientName',
  HouseAccount = 'HouseAccount',
  InvestmentTaxId = 'InvestmentTaxId',
  CRD = 'CRD',
  AccountNum = 'AccountNum',
  BrokersRepId = 'BrokersRepId',
  Email = 'Email',
}

@Component({
  selector: 'app-client-search',
  templateUrl: './client-search.component.html',
  styleUrls: ['./client-search.component.scss'],
  providers: [DictionaryPipe]
})

export class ClientSearchComponent implements OnInit, OnDestroy {
  @ViewChild('txtRepSearchFromDate') txtRepSearchFromDate;
  @ViewChild('txtRepSearchToDate') txtRepSearchToDate;
  @ViewChild('kendoGridComponent') kendoGridComponent;
  totalRecords: number;
  public loading: boolean;
  display = false;
  public alertMessage: string;
  heightOfGridContainer = 400;
  public introText: string;

  // filters
  public name: string;
  public accountNum: string;
  public userTypeList = [];
  public fundsList = [];
  public userTypeSelected: string;
  public fundSelected: string;

  // new filters
  public clientTypeNum: string;
  public houseAccount = false;
  public investmentTaxId: string;
  public crd: string;
  public brokerRepId: string;
  public email: string;

  public clientTypeLabel = '';
  public displayingFields: { [key: string]: boolean };

  currentPageNo = 1;
  rowPerPage = 50;
  skipRecords = 0;
  public sort: SortDescriptor[] = [{
    field: 'RecordName',
    dir: 'asc'
  }];

  public filteredGridData = [];
  // Add more columns in this list itself
  public consoliDatedcolumnList: Array<ColumnSettings> = [];
  public columnListBasedOnTypeSelected: Array<ColumnSettings> = [];
  private allDataLoaded = false;
  private searchRequest = new Subject();
  private tokenSub: Subscription;

  constructor(
    private abstractDataProvider: AbstractDataProvider,
    private authService: AuthServiceCustom,
    private ngxService: NgxUiLoaderService,
    private dictionaryPipe: DictionaryPipe) {

    this.consoliDatedcolumnList = [
// Investor
      {Field: 'RecordNum', Title: this.dictionaryPipe.transform('INVESTOR_ID'), Width: 1, ForType: EnUserTypes.Investor},
      {
        Field: 'RecordName',
        Title: this.dictionaryPipe.transform('INVESTOR_NAME'),
        Width: 3,
        ForType: EnUserTypes.Investor,
        IsHyperLink: true
      },
      {Field: 'CityStateZip', Title: this.dictionaryPipe.transform('CITY_STATE_ZIP'), Width: 3, ForType: EnUserTypes.Investor},
      {Field: 'Pin', Title: this.dictionaryPipe.transform('PIN'), Width: 1, ForType: EnUserTypes.Investor},
// Rep
      {Field: 'RecordName', Title: this.dictionaryPipe.transform('REP_NAME'), Width: 3, ForType: EnUserTypes.Rep, IsHyperLink: true},
      {Field: 'BkrName', Title: this.dictionaryPipe.transform('BROKER'), Width: 2, ForType: EnUserTypes.Rep},
      {Field: 'BkrRepId', Title: this.dictionaryPipe.transform('BROKER_REP_ID'), Width: 1, ForType: EnUserTypes.Rep},
      {Field: 'RegistrationId', Title: this.dictionaryPipe.transform('CRD'), Width: 1, ForType: EnUserTypes.Rep},
      {Field: 'Pin', Title: this.dictionaryPipe.transform('PIN'), Width: 1, ForType: EnUserTypes.Rep},
// Broker
      {Field: 'RecordName', Title: this.dictionaryPipe.transform('BROKER_NAME'), Width: 2, ForType: EnUserTypes.Broker, IsHyperLink: true},
      {Field: 'BkrId', Title: this.dictionaryPipe.transform('CRD'), Width: 2, ForType: EnUserTypes.Broker},
      {Field: 'Pin', Title: this.dictionaryPipe.transform('PIN'), Width: 1, ForType: EnUserTypes.Broker},
// Custodian
      {
        Field: 'RecordName',
        Title: this.dictionaryPipe.transform('CUSTODIAN_NAME'),
        Width: 2,
        ForType: EnUserTypes.Custodian,
        IsHyperLink: true
      },
      {Field: 'CustodianTaxId', Title: this.dictionaryPipe.transform('TAX_ID'), Width: 2, ForType: EnUserTypes.Custodian},
      {Field: 'CustodianQualifiedFlag', Title: this.dictionaryPipe.transform('QUALIFIED'), Width: 1, ForType: EnUserTypes.Custodian},
      {Field: 'Pin', Title: this.dictionaryPipe.transform('PIN'), Width: 1, ForType: EnUserTypes.Custodian},
// Dup Mail
      {Field: 'RecordNum', Title: this.dictionaryPipe.transform('DUPMAIL_ID'), Width: 1, ForType: EnUserTypes.DupMail},
      {
        Field: 'RecordName',
        Title: this.dictionaryPipe.transform('DUPMAIL_NAME'),
        Width: 3,
        ForType: EnUserTypes.DupMail,
        IsHyperLink: true
      },
      {Field: 'CityStateZip', Title: this.dictionaryPipe.transform('CITY_STATE_ZIP'), Width: 3, ForType: EnUserTypes.DupMail},
      {Field: 'Pin', Title: this.dictionaryPipe.transform('PIN'), Width: 1, ForType: EnUserTypes.DupMail},
      ];
  }

  ngOnInit() {
    this.heightOfGridContainer = this.authService.heightOfPageContainer - 293;
    this.getFilterCriteria();
    this.getIntroText();
    this.updateDisplayingFields();

    this.searchRequest
      .pipe(debounceTime(300))
      .subscribe(() => this.search());
    this.tokenSub = this.authService.accessTokenRemovedSubj.subscribe(() => {
      this.getFilterCriteria();
    });
  }

  ngOnDestroy() {
    if (this.tokenSub) {
      this.tokenSub.unsubscribe();
    }
  }

  getFilterCriteria() {
    forkJoin(
      this.abstractDataProvider.getData('api/ClientSearch/GetUserTypes'),
      this.abstractDataProvider.getData('api/ClientSearch/GetTheFundList'),
    ).subscribe(([userTypeList, fundsList]) => {
        this.ngxService.stop();
        this.clientTypeLabel = this.getClientTypeTitle();
        this.userTypeList = userTypeList.Data;
        if (this.userTypeList && this.userTypeList.length > 0) {
          this.userTypeSelected = this.userTypeList[0].Key;
          this.getFilteredColumnBasedOnUserType();
        }
        this.fundsList = fundsList.Data;
        const filters = this.authService.storedFilters;

        if (filters && filters.length > 0) {
          const selectedUserType = filters.find(a => a.Key === 'userTypeSelected');
          if (selectedUserType && this.userTypeList.some(u => u.Key === selectedUserType.Value)) {
            this.userTypeSelected = selectedUserType.Value;
          }
          const selectedFund = filters.find(a => a.Key === 'fundSelected');
          if (selectedFund && this.fundsList.some(u => u.FundId === selectedFund.Value)) {
            this.fundSelected = selectedFund.Value;
          }
          const name = filters.find(a => a.Key === 'name');
          if (name) {
            this.name = name.Value;
          }
          const accNum = filters.find(a => a.Key === 'accountNum');
          if (accNum) {
            this.accountNum = accNum.Value;
          }

          const clientTypeNum = filters.find(a => a.Key === 'clientTypeNum');
          if (accNum) {
            this.clientTypeNum = clientTypeNum.Value;
          }

          const houseAccount = filters.find(a => a.Key === 'houseAccount');
          if (houseAccount) {
            this.houseAccount = houseAccount.Value;
          }

          const investmentTaxId = filters.find(a => a.Key === 'investmentTaxId');
          if (investmentTaxId) {
            this.investmentTaxId = investmentTaxId.Value;
          }

          const crd = filters.find(a => a.Key === 'crd');
          if (crd) {
            this.crd = crd.Value;
          }

          const brokerRepId = filters.find(a => a.Key === 'brokerRepId');
          if (brokerRepId) {
            this.brokerRepId = brokerRepId.Value;
          }

          const email = filters.find(a => a.Key === 'email');
          if (email) {
            this.email = email.Value;
          }

        }
        this.updateDisplayingFields();
        this.search();
      },
      error => {
        this.ngxService.stop();
        this.display = true;
        this.alertMessage = getErrorMessageText(error);
      });
  }

  getIntroText() {
    this.introText = this.authService.getIntroText('CLIENTSEARCH');
  }

  onConfirm() {
    this.display = false;
  }

  public sortChange(sort: SortDescriptor[]): void {
    this.sort = sort;
    this.search();
  }

  loadMore(event) {
    if (!this.allDataLoaded && !this.loading) {
      this.skipRecords = this.filteredGridData.length;
      this.loading = true;
      this.makeAPICallGetData();
    }
  }

  downloadExel(): void {
    // TODO: impl
    const dto = this.filGenericFilterModel();
    this.loading = true;
    this.abstractDataProvider.postData(dto, 'api/ClientSearch/Download').pipe(finalize(() => this.loading = false))
      .subscribe(() => {
// TODO: impl
      }, error => {
        this.display = true;
        this.alertMessage = getErrorMessageText(error);
      });

  }

  search() {
    this.allDataLoaded = false;
    this.filteredGridData = [];
    this.currentPageNo = 1;
    this.skipRecords = 0;
    this.loading = true;
    this.makeAPICallGetData();
    this.getFilteredColumnBasedOnUserType();
    if (this.authService.UserType == "U" && this.authService.ForBrokerNum != null && (this.authService.TempUsers == null ||this.authService.TempUsers.length == 0)){
      this.authService.ForBrokerNum = null;
    }
  }

  makeAPICallGetData() {
    const dto = this.filGenericFilterModel();
    this.loading = true;
    this.abstractDataProvider.postData(dto, 'api/ClientSearch/Search')
      .pipe(
        map((data: ResponseModel<ClientSearchItemResponseModel>) => {
          // if (data && data.Data && data.Data.length) {
          //   data.Data.forEach((item: ClientSearchItemResponseModel) => item.RecordName = `${item.RecordName} (${item.RecordNum})`);
          // }
          return data;
        }),
        finalize(() => this.loading = false)
      )
      .subscribe((data: ResponseModel<ClientSearchItemResponseModel>) => {
          this.loading = false;
          if (dto.SkipRecords === 0) {
            this.totalRecords = data.TotalCount;
          }
          if (data.Data.length > 0) {
            this.filteredGridData = [...this.filteredGridData, ...data.Data];
            if (this.totalRecords === this.filteredGridData.length) {
              this.allDataLoaded = true;
            }
          } else {
            this.allDataLoaded = true;
          }
        },
        error => {
          this.loading = false;
          this.ngxService.stop();
          this.display = true;
          this.alertMessage = getErrorMessageText(error);
        });

  }

  openRecordWithDifferentRole(dataItem) {
    let recordNum = 0;
    if (this.userTypeSelected === EnUserTypes.Investor) {
      recordNum = dataItem.RecordNum;
    } else if (this.userTypeSelected === EnUserTypes.Rep) {
      recordNum = dataItem.RecordNum;
    }
    const queryParamList: Array<QueryStringParam> = [];
    queryParamList.push({Key: 'RecordNum', Value: recordNum + ''});
    queryParamList.push({Key: 'UserType', Value: this.userTypeSelected});

    this.abstractDataProvider.postData(queryParamList, 'api/Accounts/GetWpUserNumFromRecordNum')
      .subscribe(posts => {
          this.authService.navigateUrlInNewTab('investor-home?RecordNum=' + recordNum + '&UserType=' + this.userTypeSelected + '&WpUserNum=' + posts.Data, null);
        },
        error => {
          this.display = true;
          this.alertMessage = getErrorMessageText(error);
        });
  }

  openEntityRecord(dataItem) {
    const genericFilterModel = this.getFilters();
    this.abstractDataProvider.getData(`api/ClientSearch/UserNumFromRecordNumAndType?recordNum=${dataItem.RecordNum}&userType=${dataItem.RecordType}`, false
    ).subscribe(
      (posts) => {

        if (posts.Data !== 0) {
          this.authService.openApplicationWithDifferentUserType(dataItem, this.userTypeSelected, posts.Data, genericFilterModel);
        } else {
          this.display = true;
          this.alertMessage = 'User Account information not available at this time.  Contact administrator.';
        }
      }, error => {
        this.display = true;
        this.alertMessage = getErrorMessageText(error);
      });
  }

  getFilteredColumnBasedOnUserType() {
    this.columnListBasedOnTypeSelected = this.consoliDatedcolumnList.filter(a => a.ForType === this.userTypeSelected);

  }

  onFilterChange(filterText?: string, skipMinLength?: boolean) {
    if (filterText && (!skipMinLength && filterText.toString().length === 1)) {
      // no need to make request if only 1 char in a text filter
      return;
    }
    this.requestSearch();
  }

  updateDisplayingFields() {
    this.displayingFields = {
      [Field.TypeOfClient]: true,
      [Field.Fund]: true,
      [Field.ClientName]: true,
      [Field.ClientNumber]: true
    };

    switch (this.authService.UserType) {
      case EnUserTypes.Broker:
        this.displayingFields[Field.InvestmentTaxId] = this.userTypeSelected === EnUserTypes.Investor;
        this.displayingFields[Field.AccountNum] = this.userTypeSelected === EnUserTypes.Investor;
        this.displayingFields[Field.HouseAccount] = this.userTypeSelected === EnUserTypes.Investor;
        this.displayingFields[Field.Email] = true;
        this.displayingFields[Field.CRD] = this.userTypeSelected === EnUserTypes.Rep;
        this.displayingFields[Field.BrokersRepId] = this.userTypeSelected === EnUserTypes.Rep;
        break;

      case EnUserTypes.Custodian:
      case EnUserTypes.Rep:
        this.displayingFields[Field.InvestmentTaxId] = true;
        this.displayingFields[Field.AccountNum] = true;
        break;

      case EnUserTypes.User:
        this.displayingFields[Field.HouseAccount] = this.userTypeSelected === EnUserTypes.Investor;
        this.displayingFields[Field.InvestmentTaxId] = this.userTypeSelected === EnUserTypes.Investor;
        this.displayingFields[Field.AccountNum] = this.userTypeSelected === EnUserTypes.Investor;
        this.displayingFields[Field.CRD] = this.userTypeSelected === EnUserTypes.Broker || this.clientTypeNum === EnUserTypes.Rep;
        this.displayingFields[Field.BrokersRepId] = this.userTypeSelected === EnUserTypes.Rep;
        this.displayingFields[Field.Email] = this.userTypeSelected === EnUserTypes.Rep;
        break;
    }
  }

  numberOnly(event): boolean {
    const charCode = (event.which) ? event.which : event.keyCode;
    if (charCode > 31 && (charCode < 48 || charCode > 57)) {
      return false;
    }
    return true;

  }

  private filGenericFilterModel(): GenericFilterModel {
    const genericFilterModel = new GenericFilterModel();
    genericFilterModel.Filters = [];

    genericFilterModel.PageNo = this.currentPageNo;
    genericFilterModel.PageSize = this.rowPerPage;
    genericFilterModel.SortField = this.sort[0].field;
    genericFilterModel.SortOrder = this.sort[0].dir;
    genericFilterModel.SkipRecords = this.skipRecords;

    genericFilterModel.Filters.push({Key: 'userTypeSelected', Value: this.userTypeSelected});
    genericFilterModel.Filters.push({Key: 'fundSelected', Value: this.fundSelected});
    genericFilterModel.Filters.push({Key: 'recordName', Value: toUpperCase(this.name)});
    genericFilterModel.Filters.push({Key: 'recordNum', Value: toUpperCase(this.clientTypeNum)});

    // new filters
    genericFilterModel.Filters.push({Key: 'houseAccount', Value: toUpperCase(this.houseAccount.toString())});
    genericFilterModel.Filters.push({Key: 'accountNum', Value: toUpperCase(this.accountNum)});
    genericFilterModel.Filters.push({Key: 'investmentTaxId', Value: toUpperCase(this.investmentTaxId)});
    genericFilterModel.Filters.push({Key: 'crd', Value: toUpperCase(this.crd)});
    genericFilterModel.Filters.push({Key: 'brokerRepId', Value: toUpperCase(this.brokerRepId)});
    genericFilterModel.Filters.push({Key: 'email', Value: toUpperCase(this.email)});
    return genericFilterModel;
  }

  private requestSearch() {
    this.authService.storedFilters = this.getFilters();
    this.searchRequest.next();
  }

  private getFilters() {
    const genericFilterModel = [];
    genericFilterModel.push({Key: 'userTypeSelected', Value: this.userTypeSelected});
    genericFilterModel.push({Key: 'fundSelected', Value: this.fundSelected});
    genericFilterModel.push({Key: 'name', Value: this.name});
    genericFilterModel.push({Key: 'accountNum', Value: this.accountNum});

    // New filters
    genericFilterModel.push({Key: 'houseAccount', Value: this.houseAccount});
    genericFilterModel.push({Key: 'investmentTaxId', Value: this.investmentTaxId});
    genericFilterModel.push({Key: 'crd', Value: this.crd});
    genericFilterModel.push({Key: 'brokerRepId', Value: this.brokerRepId});
    genericFilterModel.push({Key: 'email', Value: this.email});
    genericFilterModel.push({Key: 'clientTypeNum', Value: this.clientTypeNum});
    return genericFilterModel;
  }

  private getClientTypeTitle(): string {
    switch (this.authService.UserType) {
      case EnUserTypes.Custodian:
      case EnUserTypes.Rep:
        return this.dictionaryPipe.transform('INVESTOR');
      default:
        return this.dictionaryPipe.transform('CLIENT');
    }
  }
}
